<div class="project-overview-view mat-elevation-z2">
  <div class="header-row">
    <h2>Project Overview by System</h2>
    <div class="download-wrap" appUserManual [userManualKey]="'project-overview-export'">
      <button class="download-button" mat-raised-button (click)="downloadSelection()" aria-label="Export to Excel">
        <mat-icon>file_download</mat-icon>
        <span>Export to Excel</span>
      </button>
    </div>
  </div>

  <div class="toolbar" appUserManual [userManualKey]="'project-overview-filters'">
    <app-filter-bar
      [searchPlaceholder]="'Search by tag number'"
      [searchValue]="search.value || ''"
      (searchChange)="search.setValue($event); $event ? findTag() : clearHighlight()"
      [quickFilters]="[
        { value: activeFilter.All, label: 'All' },
        { value: activeFilter.SignedMCC, label: 'Signed MCC', icon: 'assignment_turned_in' },
        { value: activeFilter.SignedRFCC, label: 'Signed RFCC', icon: 'rule' },
        { value: activeFilter.SignedRFOC, label: 'Signed RFOC', icon: 'verified' },
        { value: activeFilter.OpenAPunch, label: 'Open A punches', icon: 'report_problem' },
        { value: activeFilter.OpenBPunch, label: 'Open B punches', icon: 'report' },
        { value: activeFilter.RFOCSignedWithOpenPunch, label: 'RFOC signed & open punches', icon: 'error_outline' },
        { value: activeFilter.SignedChecksheets, label: 'Signed', icon: 'done_all' },
        { value: activeFilter.VerifiedChecksheets, label: 'Verified', icon: 'verified_user' },
        { value: activeFilter.SignedNotVerified, label: 'Signed ¬ Verified', icon: 'priority_high' },
        { value: activeFilter.Errors, label: 'Errors', icon: 'bug_report' },
      ]"
      [selectedQuickFilters]="[filterControl.value]"
      [multiSelect]="false"
      (quickFiltersChange)="setFilterFromString($event[0])"
    ></app-filter-bar>
  </div>

  <ng-container *ngIf="groups$ | async as groups">
    <div *ngIf="groups.length === 0" class="empty">No overview data.</div>

    <div *ngFor="let group of groups; trackBy: trackBySystem" class="system-group" [ngClass]="getSystemAlertClass(group)">
      <div class="system-header" (click)="toggleGroup(group.system)" role="button" tabindex="0">
        <div class="system-title">System: {{ group.system }}</div>
        <div class="system-progress" *ngIf="group.items.length">
          <div
            class="progress-track"
            role="progressbar"
            [attr.aria-valuenow]="getGroupProgress(group)"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div class="progress-fill" [style.width.%]="getGroupProgress(group)"></div>
          </div>
          <div class="progress-label">{{ getGroupProgress(group) }}%</div>
        </div>
        <div class="system-meta">
          <ng-container [ngSwitch]="group.status">
            <span *ngSwitchCase="'pending'">Awaiting load</span>
            <span *ngSwitchCase="'loading'">Loading…</span>
            <ng-container *ngSwitchCase="'loaded'">
              <span *ngIf="!group.items.length">Loaded (no checksheets)</span>
              <span *ngIf="group.items.length">{{ getGroupCount(group) }} items</span>
            </ng-container>
            <span *ngSwitchCase="'error'">Error loading system</span>
            <span *ngSwitchDefault>{{ getGroupCount(group) }} items</span>
          </ng-container>
        </div>
        <div class="punch-summary" aria-label="Punch summary">
          <div class="punch-line">
            <span class="label">A</span>
            <span class="val a-entered" title="A Entered"><mat-icon>playlist_add</mat-icon> {{ group.punch.aEntered }}</span>
            <span class="val a-accepted" title="A Accepted"><mat-icon>task_alt</mat-icon> {{ group.punch.aAccepted }}</span>
            <span class="val a-cleared" title="A Cleared"><mat-icon>check_circle</mat-icon> {{ group.punch.aCleared }}</span>
          </div>
          <div class="punch-line">
            <span class="label">B</span>
            <span class="val b-entered" title="B Entered"><mat-icon>playlist_add</mat-icon> {{ group.punch.bEntered }}</span>
            <span class="val b-accepted" title="B Accepted"><mat-icon>task_alt</mat-icon> {{ group.punch.bAccepted }}</span>
            <span class="val b-cleared" title="B Cleared"><mat-icon>check_circle</mat-icon> {{ group.punch.bCleared }}</span>
          </div>
          <div class="punch-total" title="Total Punch Count">
            <mat-icon>summarize</mat-icon> {{ group.punch.total }}
            <mat-icon *ngIf="hasPunchMismatch(group)" class="mismatch" title="Mismatch in punch totals">warning</mat-icon>
          </div>
        </div>
        <div class="certificates-summary" aria-label="Certificates summary">
          <div class="cert-row">
            <span class="label">MCC</span>
            <span class="val-block">
              <span class="val" title="Issued">Issued {{ group.certificates.mcc.issued }}</span>
              <span class="val" title="Signed">Signed {{ group.certificates.mcc.signed }}</span>
            </span>
          </div>
          <div class="cert-row">
            <span class="label">RFCC</span>
            <span class="val-block">
              <span class="val" title="Issued">Issued {{ group.certificates.rfcc.issued }}</span>
              <span class="val" title="Signed">Signed {{ group.certificates.rfcc.signed }}</span>
            </span>
          </div>
          <div class="cert-row">
            <span class="label">RFOC</span>
            <span class="val-block">
              <span class="val" title="Issued">Issued {{ group.certificates.rfoc.issued }}</span>
              <span class="val" title="Signed">Signed {{ group.certificates.rfoc.signed }}</span>
            </span>
            <mat-icon *ngIf="hasRfocOpenAPunch(group)" class="alert-a" title="RFOC signed but open A punches">warning</mat-icon>
            <mat-icon *ngIf="hasRfocOpenBPunch(group)" class="alert-b" title="RFOC signed but open B punches">warning</mat-icon>
          </div>
          <div class="cert-row">
            <span class="label">None</span>
            <span class="val" title="Checksheets not in any certificate"
              >{{ group.certificates.none.count }} / {{ group.certificates.none.total }}</span
            >
          </div>
        </div>
        <div class="checksheets-summary" aria-label="Checksheet signed summary">
          <span class="val">Signed MCC {{ group.checksheetsSigned.mcc }}</span>
          <span class="val">Signed RFCC {{ group.checksheetsSigned.rfcc }}</span>
          <span class="val">Signed RFOC {{ group.checksheetsSigned.rfoc }}</span>
        </div>
        <button mat-icon-button class="toggle-btn" aria-label="Toggle group">
          <mat-icon>{{ group.expanded ? 'expand_less' : 'expand_more' }}</mat-icon>
        </button>
      </div>

      <div class="system-items" *ngIf="group.expanded">
        <ng-container [ngSwitch]="group.status">
          <div *ngSwitchCase="'pending'" class="empty">System is queued for loading.</div>
          <div *ngSwitchCase="'loading'" class="empty">Loading system overview…</div>
          <div *ngSwitchCase="'error'" class="empty">Failed to load this system. Try again later.</div>
          <ng-container *ngSwitchDefault>
            <table class="items-table mat-elevation-z1" *ngIf="group.items.length">
              <thead>
                <tr>
                  <th>CommPkg</th>
                  <th>McPkg</th>
                  <th>Tag</th>
                  <th>CheckSheet</th>
                  <th class="col-status">Status</th>
                  <th class="col-progress">Progress</th>
                  <th>Signed/Verified</th>
                  <th class="col-certs">Certificates</th>
                  <th class="col-alerts">Alerts</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  *ngFor="let item of group.items; let i = index; trackBy: trackByItem"
                  [attr.id]="'overview-item-' + item.id"
                  [class.highlight]="isHighlighted(item)"
                  [class.cp-group-start]="i === 0 || group.items[i - 1].commissioningPackageNumber !== item.commissioningPackageNumber"
                  [ngClass]="getChecksheetClass(item)"
                  [ngStyle]="getChecksheetTintStyle(item)"
                >
                  <td class="cp-cell">{{ item.commissioningPackageNumber }}</td>
                  <td>{{ item.mcPackageNumber }}</td>
                  <td>{{ item.tagNumber || '-' }}</td>
                  <td>{{ item.checkSheetNumber }}</td>
                  <td class="col-status"><app-status-circle [status]="item.status"></app-status-circle></td>
                  <td class="col-progress">
                    <div
                      class="progress-track"
                      role="progressbar"
                      [attr.aria-valuenow]="itemProgressPercent(item)"
                      aria-valuemin="0"
                      aria-valuemax="100"
                    >
                      <div class="progress-fill" [style.width.%]="itemProgressPercent(item)"></div>
                    </div>
                  </td>
                  <td>
                    <div class="assignments">
                      <app-user-assignment
                        [timestamp]="item.signedByTimestamp"
                        [fullName]="item.signedByUserName"
                        [useDate]="true"
                        [displayOption]="'small'"
                      ></app-user-assignment>
                      <app-user-assignment
                        [timestamp]="item.verifiedByTimestamp"
                        [fullName]="item.verifiedByUserName"
                        [useDate]="true"
                        [displayOption]="'small'"
                      ></app-user-assignment>
                    </div>
                  </td>
                  <td class="certs-cell col-certs">
                    <mat-icon
                      class="cert mcc"
                      [class.active]="!!item.mccCertificateNumber && !isCpCheckSheet(item)"
                      [class.cp-hidden]="isCpCheckSheet(item)"
                      title="MCC certificate"
                    >
                      assignment_turned_in
                    </mat-icon>
                    <mat-icon
                      class="cert rfcc"
                      [class.active]="!!item.rfccCertificateNumber && !isCpCheckSheet(item)"
                      [class.cp-hidden]="isCpCheckSheet(item)"
                      title="RFCC certificate"
                    >
                      rule
                    </mat-icon>
                    <mat-icon class="cert rfoc" [class.active]="!!item.rfocCertificateNumber" title="RFOC certificate">verified</mat-icon>
                  </td>
                  <td class="alerts-cell col-alerts">
                    <mat-icon *ngIf="hasVerifiedNotSigned(item)" class="alert-warn" title="Verified but not signed">warning</mat-icon>
                    <mat-icon *ngIf="hasSignedProgressIncomplete(item)" class="alert-warn" title="Signed but progress < 100%"
                      >warning</mat-icon
                    >
                    <mat-icon *ngIf="hasRfocWithoutPrereqs(item)" class="alert-error" title="RFOC present without RFCC/MCC and not CP"
                      >error</mat-icon
                    >
                    <mat-icon *ngIf="hasPunchStatusMismatchA(item)" class="alert-error" title="Open A punches but status is not PA"
                      >error</mat-icon
                    >
                    <mat-icon *ngIf="hasPunchStatusMismatchB(item)" class="alert-error" title="Open B punches but status is not PB"
                      >error</mat-icon
                    >
                    <mat-icon
                      *ngIf="hasCertificateNotSignedAndVerified(item)"
                      class="alert-warn"
                      title="In certificate but not both signed and verified"
                      >warning</mat-icon
                    >
                  </td>
                </tr>
              </tbody>
            </table>
            <div *ngIf="!group.items.length" class="empty">No items in this system.</div>
          </ng-container>
        </ng-container>
      </div>
    </div>
  </ng-container>
</div>
